import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import { useSelector } from 'react-redux';
import { ExpandablePanel } from '../../../components/widgets';
import { getPostbackMacroNetworkName } from '../../../selectors/partnerIntegration';
import { getPlatformIcon } from '../../../utils/OS';
import { ATTRIBUTION_TAB_IDS, TRANSLATE_PREFIX } from '../../consts';
import { urlField } from '../../useFieldValidity';
import { useFormDataUpdater } from '../../formUpdaters';
import {
    applyPostbackEventsPlatformsNext,
    buildNextEntriesAfterRemovingCombo,
    getDeselectIssues,
} from '../../postbackEventsPlatformsUpdates';
import DeleteIcon from '../DeleteIcon';
import PostbackTemplateMacroTable from './PostbackTemplateMacroTable';
import CopyTemplatePanel from './CopyTemplatePanel';
import UrlPreview from '../UrlPreview';
import css from './PostbackTemplatesTab.css';

const POSTBACK_PLACEHOLDER_BASE = 'https://your-domain.com/postback';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_TEMPLATES_TAB`;
const POSTBACK_TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_OVERALL_SETUP_TAB`;
const OVERALL_CONFIRM_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_OVERALL_SETUP_TAB.CONFIRM_DIALOG`;
const PLATFORM_LABEL_PREFIX = `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORM_OPTIONS`;
const POSTBACK_OVERALL_TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;
const POSTBACK_TEMPLATES_TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES;

const PostbackTemplateCard = ({ eventType, platform, expanded, onToggle, readOnly, translate }) => {
    const { values } = useFormikContext();
    const postbackMacroNetworkName = useSelector(getPostbackMacroNetworkName);
    const updateFormData = useFormDataUpdater();
    const overallTab = values.formData?.[POSTBACK_OVERALL_TAB_ID] || {};
    const templatesTab = values.formData?.[POSTBACK_TEMPLATES_TAB_ID] || {};
    const rows = templatesTab?.[eventType]?.[platform] || [];
    const paramsCount = rows.filter(r => !!r.partner_parameter_key?.trim()).length;
    const baseUrl = overallTab.different_domains_per_postback
        ? overallTab[urlField(eventType)]
        : overallTab.postback_url_base;

    const PlatformIcon = getPlatformIcon(platform);
    const eventLabel = translate(`${POSTBACK_TAB_PREFIX}.EVENT_TYPES.${eventType.toUpperCase()}`);
    const platformLabel = translate(`${PLATFORM_LABEL_PREFIX}.${platform.toUpperCase()}`);

    const getConfirmOptions = useCallback(() => {
        const overallSetupTab = values.formData?.[POSTBACK_OVERALL_TAB_ID] || {};
        const eventsAndPlatforms = overallSetupTab.postback_events_and_platforms || [];
        const templatesFormData = values.formData?.[POSTBACK_TEMPLATES_TAB_ID];
        const next = buildNextEntriesAfterRemovingCombo(eventsAndPlatforms, eventType, platform);
        const issues = getDeselectIssues({
            tabData: overallSetupTab,
            eventsAndPlatforms,
            next,
            templatesFormData,
            toggledEventType: eventType,
        });

        if (issues.length === 0) {
            return {
                titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DELETE_PLATFORM_TEMPLATE_TITLE`,
                textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DELETE_PLATFORM_TEMPLATE_TEXT`,
            };
        }
        if (issues.length === 1) {
            const key = issues[0];
            return {
                titleKey: `${OVERALL_CONFIRM_PREFIX}.${key}_TITLE`,
                textKey: `${OVERALL_CONFIRM_PREFIX}.${key}_TEXT`,
            };
        }
        return {
            titleKey: `${OVERALL_CONFIRM_PREFIX}.DESELECT_MULTIPLE_TITLE`,
            body: (
                <>
                    <Translate id={`${OVERALL_CONFIRM_PREFIX}.DESELECT_MULTIPLE_INTRO`} />
                    <ul>
                        {issues.map(key => (
                            <li key={key}>
                                <Translate id={`${OVERALL_CONFIRM_PREFIX}.${key}_BULLET`} />
                            </li>
                        ))}
                    </ul>
                    <Translate id={`${OVERALL_CONFIRM_PREFIX}.DESELECT_MULTIPLE_OUTRO`} />
                </>
            ),
        };
    }, [values.formData, eventType, platform]);

    const handleRemoveTemplate = useCallback(() => {
        updateFormData(prev =>
            applyPostbackEventsPlatformsNext(
                prev,
                buildNextEntriesAfterRemovingCombo(
                    prev[POSTBACK_OVERALL_TAB_ID]?.postback_events_and_platforms || [],
                    eventType,
                    platform
                ),
                postbackMacroNetworkName
            )
        );
    }, [updateFormData, eventType, platform, postbackMacroNetworkName]);

    return (
        <ExpandablePanel
            expanded={expanded}
            onExpandClick={onToggle}
            className={css.templateCard}
            headerContainerClassName={css.templateCardHeader}
            overrideClasses={{
                contentArea: css.templateCardContent,
                arrowArea: css.templateCardArrow,
                externalHeaderArea: css.templateCardHeaderArea,
            }}
            header={
                <div className={css.templateHeaderRow}>
                    <div className={css.templateHeader}>
                        <div className={classNames(css.platformIconWrapper, css[platform])}>
                            <PlatformIcon className={css.platformIcon} />
                        </div>
                        <div className={css.templateTitleBlock}>
                            <span className={css.templateTitle}>{`${eventLabel} - ${platformLabel}`}</span>
                            <span className={css.templateSubtitle}>
                                <Translate id={`${TAB_PREFIX}.PARAMETERS_CONFIGURED`} data={{ count: paramsCount }} />
                            </span>
                        </div>
                    </div>
                    {!readOnly && (
                        <DeleteIcon
                            icon="trash"
                            className={css.templateDeleteIcon}
                            getConfirmOptions={getConfirmOptions}
                            onConfirm={handleRemoveTemplate}
                        />
                    )}
                </div>
            }
        >
            <div className={css.templateBody}>
                <div className={css.previewSection}>
                    <div className={css.sectionLabel}>
                        <Translate id={`${TAB_PREFIX}.PREVIEW_LABEL`} />
                    </div>
                    <UrlPreview
                        baseUrl={baseUrl?.trim() || POSTBACK_PLACEHOLDER_BASE}
                        pairs={rows.map(r => ({ key: r.partner_parameter_key, value: r.singular_macro }))}
                    />
                </div>
                <div className={css.mappingSection}>
                    <div className={css.sectionLabel}>
                        <Translate id={`${TAB_PREFIX}.MAPPING_LABEL`} />
                    </div>
                    <PostbackTemplateMacroTable eventType={eventType} platform={platform} readOnly={readOnly} />
                </div>
                <CopyTemplatePanel sourceEventType={eventType} sourcePlatform={platform} readOnly={readOnly} />
            </div>
        </ExpandablePanel>
    );
};

PostbackTemplateCard.propTypes = {
    eventType: PropTypes.string.isRequired,
    platform: PropTypes.string.isRequired,
    expanded: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(PostbackTemplateCard);
